import { useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  ChevronRight, 
  Lock, 
  Package, 
  Network,
  Cable,
  ClipboardList,
  Code2,
  FileText,
  Shield,
  Users,
  RefreshCw,
  Server,
  HardDrive,
  Boxes,
  CheckCircle2,
  ArrowRight,
  Play,
  Search,
  Plug,
  Zap,
  Cpu,
  Eye,
  Radar,
  LayoutGrid
} from 'lucide-react';
import MarketingHeader from '@/components/marketing/MarketingHeader';
import MarketingFooter from '@/components/marketing/MarketingFooter';
import { JsonLd, organizationSchema, softwareSchema } from '@/components/seo/JsonLd';

// Animated section wrapper
function AnimatedSection({ children, className = "", delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setIsVisible(true), delay);
        }
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [delay]);

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${className}`}
    >
      {children}
    </div>
  );
}

const featureLinks = [
  { title: 'Asset Inventory', description: 'Track every server, switch, and device across your entire infrastructure with complete lifecycle management.', href: '/features/inventory', icon: Package, color: 'from-blue-500 to-cyan-500' },
  { title: 'IPAM', description: 'Manage IP addresses, subnets, VLANs, and network allocations with zero conflicts.', href: '/features/ipam', icon: Network, color: 'from-violet-500 to-purple-500' },
  { title: 'Cable Management', description: 'Document every connection from port to port with visual patch panel views.', href: '/features/cables', icon: Cable, color: 'from-emerald-500 to-green-500' },
  { title: 'Change Management', description: 'ITIL-aligned workflows with approvals, scheduling, and complete audit trails.', href: '/features/changes', icon: ClipboardList, color: 'from-orange-500 to-amber-500' },
  { title: 'Network Discovery', description: 'Automatically scan and discover devices on your network with IP range scanning and SNMP polling.', href: '/features/discovery', icon: Radar, color: 'from-cyan-500 to-teal-500' },
  { title: '3D Visualization', description: 'Interactive 3D floor plans with thermal overlays, cable paths, and drag-and-drop rack planning.', href: '/features/visualization', icon: Eye, color: 'from-pink-500 to-rose-500' },
  { title: 'Power Monitoring', description: 'Real-time power consumption tracking, PUE metrics, and capacity alerts at PDU and circuit level.', href: '/features/monitoring', icon: Zap, color: 'from-yellow-500 to-orange-500' },
  { title: 'Integration Hub', description: 'Pre-built connectors for ServiceNow, Jira, Slack, and 15+ enterprise platforms.', href: '/features/integrations', icon: Plug, color: 'from-indigo-500 to-violet-500' },
  { title: 'API & Webhooks', description: 'RESTful API with full CRUD operations and real-time webhook notifications.', href: '/features/api', icon: Code2, color: 'from-rose-500 to-pink-500' },
  { title: 'Reports & Analytics', description: 'Scheduled reports, KPI dashboards, and data exports in multiple formats.', href: '/features/reports', icon: FileText, color: 'from-sky-500 to-blue-500' },
];

// Platform capabilities - honest feature highlights
const capabilities = [
  { label: '10 Integrated Modules', description: 'Assets, IPAM, Cables, Changes, Discovery, 3D, Power, Integrations, API, Reports', icon: Boxes },
  { label: 'Unlimited Assets', description: 'Scale from 10 to 100,000+ assets', icon: Server },
  { label: 'Global Search', description: 'Sub-200ms search across all data', icon: Search },
  { label: 'Real-time Monitoring', description: 'Live power, thermal, and status data', icon: Zap },
];

const benefits = [
  { icon: Lock, title: 'Row-Level Security', description: 'Each organization\'s data is completely isolated at the database level.' },
  { icon: Users, title: 'Role-Based Access', description: 'Granular permissions from viewer to admin with custom user groups.' },
  { icon: Shield, title: 'Complete Audit Trail', description: 'Every change is logged with user, timestamp, and before/after values.' },
  { icon: RefreshCw, title: 'API Integration', description: 'Sync data from external systems via scheduled API pulls.' },
];

const workflowSteps = [
  { step: 1, title: 'Import Your Assets', description: 'CSV upload, API sync, or auto-discover devices on your network.', icon: HardDrive },
  { step: 2, title: 'Organize Hierarchy', description: 'Structure by data center, hall, row, and rack with 3D visualization.', icon: Boxes },
  { step: 3, title: 'Connect Systems', description: 'Document cables, IPs, and integrate with your existing tools.', icon: Cable },
  { step: 4, title: 'Monitor & Manage', description: 'Track power, changes, and get real-time alerts on capacity.', icon: Zap },
];

export default function LandingPage() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'IMRACK® | Enterprise Data Center Infrastructure Management (DCIM) Software';
  }, []);

  return (
    <div className="min-h-screen bg-background overflow-hidden">
      <JsonLd data={organizationSchema} />
      <JsonLd data={softwareSchema} />
      
      {/* Animated background */}
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-primary/5" />
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-info/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 right-1/3 w-48 h-48 bg-success/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }} />
      </div>

      <MarketingHeader />

      <main>
        {/* Hero Section */}
        <section className="container mx-auto px-4 sm:px-6 pt-12 pb-16 lg:pt-24 lg:pb-32">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="text-center lg:text-left">
              <Badge variant="secondary" className="mb-4 sm:mb-6 gap-2 animate-fade-in">
                Enterprise DCIM Platform
              </Badge>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold tracking-tight text-foreground animate-fade-in-up leading-[1.05]" style={{ animationDelay: '0.1s' }}>
                Control Your
              </h1>
              <h2 className="mt-1 sm:mt-2 text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold tracking-tight text-gradient bg-gradient-to-r from-primary via-info to-primary bg-[length:200%_auto] animate-gradient-shift leading-[1.05]" style={{ animationDelay: '0.15s' }}>
                Data Center
              </h2>
              <h3 className="mt-1 sm:mt-2 text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold tracking-tight text-foreground animate-fade-in-up leading-[1.05]" style={{ animationDelay: '0.2s' }}>
                Infrastructure
              </h3>
              <p className="mt-5 sm:mt-6 text-base sm:text-lg text-muted-foreground max-w-xl animate-fade-in-up" style={{ animationDelay: '0.25s' }}>
                The complete DCIM solution for managing assets, networks, cables, power, and changes—with real-time monitoring, 3D visualization, and 15+ enterprise integrations.
              </p>
              <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row items-center gap-3 sm:gap-4 lg:justify-start justify-center animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
                <Button size="lg" onClick={() => navigate('/features')} className="gap-2 group w-full sm:w-auto">
                  Explore Features
                  <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
                <Button size="lg" variant="outline" onClick={() => navigate('/contact')} className="gap-2 w-full sm:w-auto">
                  <Play className="h-4 w-4" />
                  Request Demo
                </Button>
              </div>
            </div>

            {/* Hero Graphic - Animated rack visualization */}
            <div className="relative hidden lg:block animate-fade-in-right" style={{ animationDelay: '0.4s' }}>
              <div className="relative bg-gradient-to-br from-card to-muted/50 rounded-2xl p-8 border shadow-xl">
                {/* Rack visualization */}
                <div className="grid grid-cols-3 gap-4">
                  {[...Array(3)].map((_, rackIndex) => (
                    <div key={rackIndex} className="bg-background rounded-lg p-2 shadow-inner" style={{ animationDelay: `${0.5 + rackIndex * 0.1}s` }}>
                      <div className="text-xs text-center text-muted-foreground mb-2 font-mono">Rack {rackIndex + 1}</div>
                      <div className="space-y-1">
                        {[...Array(8)].map((_, unitIndex) => {
                          const filled = Math.random() > 0.3;
                          const isServer = Math.random() > 0.5;
                          return (
                            <div
                              key={unitIndex}
                              className={`h-4 rounded transition-all duration-500 ${
                                filled
                                  ? isServer
                                    ? 'bg-gradient-to-r from-primary/80 to-primary/60'
                                    : 'bg-gradient-to-r from-info/80 to-info/60'
                                  : 'bg-muted/50'
                              }`}
                              style={{ 
                                animationDelay: `${0.6 + unitIndex * 0.05 + rackIndex * 0.1}s`,
                                opacity: 0,
                                animation: `fade-in 0.3s ease-out ${0.6 + unitIndex * 0.05 + rackIndex * 0.1}s forwards`
                              }}
                            >
                              {filled && (
                                <div className="flex items-center justify-between px-1 h-full">
                                  <div className="flex gap-0.5">
                                    {[...Array(3)].map((_, i) => (
                                      <div key={i} className="w-1 h-1 rounded-full bg-success animate-pulse-glow" style={{ animationDelay: `${i * 0.2}s` }} />
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Network connections animation */}
                <div className="absolute -right-4 top-1/2 -translate-y-1/2 flex flex-col gap-2">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="flex items-center gap-1">
                      <div className="w-8 h-0.5 bg-gradient-to-r from-primary to-transparent animate-pulse-glow" style={{ animationDelay: `${i * 0.3}s` }} />
                      <div className="w-2 h-2 rounded-full bg-primary animate-pulse" style={{ animationDelay: `${i * 0.3}s` }} />
                    </div>
                  ))}
                </div>
                
                {/* Info overlay - example data */}
                <div className="absolute -bottom-4 -left-4 bg-card rounded-lg p-3 shadow-lg border animate-float">
                  <div className="flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-success/20">
                      <CheckCircle2 className="h-4 w-4 text-success" />
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">3D View</div>
                      <div className="text-sm font-semibold">Live Status</div>
                    </div>
                  </div>
                </div>
                
                <div className="absolute -top-4 -right-4 bg-card rounded-lg p-3 shadow-lg border animate-float" style={{ animationDelay: '0.5s' }}>
                  <div className="flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/20">
                      <Zap className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Power</div>
                      <div className="text-sm font-semibold">Real-time</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Capabilities Section */}
        <section className="container mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <AnimatedSection>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
              {capabilities.map((cap, index) => (
                <div key={index} className="text-center p-4 sm:p-6 rounded-xl bg-gradient-to-br from-card to-muted/30 border">
                  <div className="flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-lg sm:rounded-xl bg-primary/10 mx-auto mb-2 sm:mb-3">
                    <cap.icon className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
                  </div>
                  <div className="text-sm sm:text-lg font-bold text-foreground">{cap.label}</div>
                  <div className="text-xs sm:text-sm text-muted-foreground hidden sm:block">{cap.description}</div>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </section>

        {/* Features Grid */}
        <section className="container mx-auto px-6 py-16">
          <AnimatedSection className="text-center mb-12">
            <Badge variant="secondary" className="mb-4">Complete Platform</Badge>
            <h2 className="text-3xl font-bold text-foreground mb-4">
              Everything You Need to Manage Your Infrastructure
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Ten integrated modules working together to give you complete visibility and control over your data center operations.
            </p>
          </AnimatedSection>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {featureLinks.slice(0, 8).map((feature, index) => (
              <AnimatedSection key={feature.href} delay={index * 75}>
                <Card 
                  className="group cursor-pointer transition-all duration-300 hover:shadow-xl hover:-translate-y-1 overflow-hidden h-full"
                  onClick={() => navigate(feature.href)}
                >
                  <CardContent className="p-6">
                    <div className={`inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${feature.color} mb-4 transition-transform group-hover:scale-110`}>
                      <feature.icon className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                      {feature.title}
                    </h3>
                    <p className="text-muted-foreground text-sm leading-relaxed line-clamp-3">
                      {feature.description}
                    </p>
                    <div className="mt-4 flex items-center text-sm text-primary font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                      Learn more <ArrowRight className="ml-1 h-4 w-4" />
                    </div>
                  </CardContent>
                </Card>
              </AnimatedSection>
            ))}
          </div>

          {/* Additional features row */}
          <div className="grid gap-6 md:grid-cols-2 mt-6">
            {featureLinks.slice(8).map((feature, index) => (
              <AnimatedSection key={feature.href} delay={(index + 8) * 75}>
                <Card 
                  className="group cursor-pointer transition-all duration-300 hover:shadow-xl hover:-translate-y-1 overflow-hidden"
                  onClick={() => navigate(feature.href)}
                >
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className={`inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${feature.color} shrink-0 transition-transform group-hover:scale-110`}>
                      <feature.icon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-foreground mb-1 group-hover:text-primary transition-colors">
                        {feature.title}
                      </h3>
                      <p className="text-muted-foreground text-sm leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </AnimatedSection>
            ))}
          </div>
        </section>

        {/* How It Works */}
        <section className="container mx-auto px-6 py-16">
          <AnimatedSection className="text-center mb-12">
            <Badge variant="secondary" className="mb-4">Simple Workflow</Badge>
            <h2 className="text-3xl font-bold text-foreground mb-4">
              Get Started in Minutes
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              From import to insights, IMRACK streamlines every step of infrastructure management.
            </p>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {workflowSteps.map((step, index) => (
              <AnimatedSection key={step.step} delay={index * 150}>
                <div className="relative">
                  {/* Connector line */}
                  {index < workflowSteps.length - 1 && (
                    <div className="hidden lg:block absolute top-10 left-full w-full h-0.5 bg-gradient-to-r from-primary/50 to-transparent z-0" />
                  )}
                  <div className="relative z-10 text-center">
                    <div className="inline-flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 mb-4 border border-primary/20">
                      <step.icon className="h-8 w-8 text-primary" />
                    </div>
                    <div className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-bold -mt-4 -ml-4 relative z-20">
                      {step.step}
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2 mt-2">{step.title}</h3>
                    <p className="text-sm text-muted-foreground">{step.description}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </section>

        {/* Benefits Section */}
        <section className="container mx-auto px-6 py-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection>
              <Badge variant="secondary" className="mb-4">Enterprise Ready</Badge>
              <h2 className="text-3xl font-bold text-foreground mb-4">
                Built for Security & Compliance
              </h2>
              <p className="text-muted-foreground mb-8">
                IMRACK is designed from the ground up to meet strict enterprise security requirements. Your data is protected at every layer.
              </p>
              <div className="grid sm:grid-cols-2 gap-6">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 shrink-0">
                      <benefit.icon className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground">{benefit.title}</h3>
                      <p className="text-sm text-muted-foreground">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </AnimatedSection>

            <AnimatedSection delay={200}>
              <div className="relative">
                <div className="bg-gradient-to-br from-card to-muted/30 rounded-2xl p-8 border">
                  <div className="space-y-4">
                    {[
                      { label: 'RLS Enabled', value: 'All Tables', color: 'bg-success' },
                      { label: 'Audit Logging', value: 'Every Change', color: 'bg-info' },
                      { label: 'API Rate Limit', value: '1000/min', color: 'bg-primary' },
                      { label: 'Data Encryption', value: 'AES-256', color: 'bg-warning' },
                    ].map((item, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-background rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className={`w-2 h-2 rounded-full ${item.color}`} />
                          <span className="text-sm font-medium">{item.label}</span>
                        </div>
                        <Badge variant="secondary">{item.value}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="absolute -top-4 -right-4 bg-card rounded-lg p-3 shadow-lg border animate-float">
                  <div className="flex items-center gap-2">
                    <Shield className="h-5 w-5 text-success" />
                    <span className="text-sm font-semibold">SOC 2 Ready</span>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </section>

        {/* CTA Section */}
        <section className="container mx-auto px-6 py-16 sm:py-20">
          <AnimatedSection>
            <Card className="overflow-hidden border-2 border-primary/20 bg-gradient-to-br from-primary/5 via-background to-primary/10">
              <CardContent className="p-8 sm:p-12 text-center">
                <Badge variant="secondary" className="mb-4">Get Started</Badge>
                <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4">
                  Ready to take control of your infrastructure?
                </h2>
                <p className="text-muted-foreground max-w-2xl mx-auto mb-8 text-base sm:text-lg">
                  Join enterprises using IMRACK® to manage their data centers with confidence. Contact us for a demo and custom pricing.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Button size="lg" onClick={() => navigate('/contact')} className="gap-2 group w-full sm:w-auto">
                    Contact Sales
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Button>
                  <Button size="lg" variant="outline" onClick={() => navigate('/features')} className="w-full sm:w-auto">
                    Explore All Features
                  </Button>
                </div>
              </CardContent>
            </Card>
          </AnimatedSection>
        </section>
      </main>

      <MarketingFooter />
    </div>
  );
}
